import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
// import { setIntroCompleted } from '../redux/actions/userActions'; // Предполагаем, что у вас есть такой action
import './IntroSlider.css'; // Убедитесь, что этот CSS файл существует и содержит нужные стили

// Пути к изображениям слайдера (убедитесь, что они верны и файлы существуют в public/start_pages/)
const slide1 = '/start_pages/start_01.jpg';
const slide2 = '/start_pages/start_02.jpg';
const slide3 = '/start_pages/start_03.jpg';
const slide4 = '/start_pages/start_04.jpg';
const slide5 = '/start_pages/start_05.jpg';

const slides = [slide1, slide2, slide3, slide4, slide5];

// ---> ВЕРСИЯ ДЛЯ ОБХОДА КЭША <---
// !!! Меняйте эту строку (например, на "v1.2", "v1.3") каждый раз,
// !!! когда вы обновляете ФАЙЛЫ изображений start_0X.jpg
const CACHE_VERSION = "v1.4";

/**
 * Компонент полноэкранного слайдера для отображения вводных инструкций
 * @param {Object} props - Свойства компонента
 * @param {Function} props.onComplete - Функция, вызываемая при завершении просмотра слайдов
 */
const IntroSlider = ({ onComplete }) => {
  const dispatch = useDispatch();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const mouseStartX = useRef(0);
  const mouseEndX = useRef(0);
  const [imageErrors, setImageErrors] = useState({}); // Состояние для отслеживания ошибок загрузки

  // Функция для получения URL с параметром версии/времени
  const getImageUrlWithCacheBuster = useCallback((url, isRetry = false) => {
      if (!url) return ''; // Возвращаем пустую строку, если URL не предоставлен
      const separator = url.includes('?') ? '&' : '?';
      const cacheParam = isRetry
          ? `${CACHE_VERSION}&t=${Date.now()}` // Добавляем время при повторе
          : CACHE_VERSION; // При обычной загрузке - только версию
      return `${url}${separator}v=${cacheParam}`;
  }, []); // Пустая зависимость

  // Обработчик ошибок для <img>
  const handleImageError = useCallback((e, slideIndex) => {
    const erroredSrc = e.target.src;
    console.error(`Ошибка загрузки изображения ${slideIndex}: ${erroredSrc}`);

    // Проверяем, не пытались ли мы уже перезагрузить это изображение
    if (!imageErrors[slideIndex]) {
        console.log(`Попытка перезагрузки изображения ${slideIndex} с cache buster...`);
        setImageErrors(prev => ({ ...prev, [slideIndex]: true })); // Помечаем, что была ошибка
        // Пытаемся установить новый src с параметром времени для обхода кэша
        // Важно: убедимся, что используем оригинальный путь из slides[], а не уже модифицированный erroredSrc
        e.target.src = getImageUrlWithCacheBuster(slides[slideIndex], true);
    } else {
        console.warn(`Повторная ошибка загрузки ${slideIndex}, показываем плейсхолдер (если он есть в CSS).`);
        // Если повторная попытка не удалась, можно добавить класс для отображения плейсхолдера через CSS
        e.target.classList.add('image-error-placeholder');
        // Можно также скрыть img тег, если плейсхолдер реализован через background-image контейнера
        // e.target.style.display = 'none';
    }
  }, [imageErrors, getImageUrlWithCacheBuster]); // Добавляем imageErrors и getImageUrlWithCacheBuster в зависимости

  const getPrevSlideIndex = () => {
    return currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
  };

  const getNextSlideIndex = () => {
    return currentSlide === slides.length - 1 ? 0 : currentSlide + 1;
  };

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setAnimationClass('sliding-left');
    setTimeout(() => {
      setCurrentSlide(prevSlide => (prevSlide === slides.length - 1 ? 0 : prevSlide + 1));
      setAnimationClass('');
      setIsAnimating(false);
    }, 350); // Время должно совпадать с transition в CSS
  }, [isAnimating]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setAnimationClass('sliding-right');
    setTimeout(() => {
      setCurrentSlide(prevSlide => (prevSlide === 0 ? slides.length - 1 : prevSlide - 1));
      setAnimationClass('');
      setIsAnimating(false);
    }, 350); // Время должно совпадать с transition в CSS
  }, [isAnimating]);

  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setAnimationClass(index > currentSlide ? 'sliding-left' : 'sliding-right');
    setTimeout(() => {
      setCurrentSlide(index);
      setAnimationClass('');
      setIsAnimating(false);
    }, 350);
  };

  const completeIntro = useCallback(() => {
    setFadeOut(true);
    setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
      // Сохраняем в localStorage, что интро пройдено
      try {
          localStorage.setItem('introCompleted', 'true');
          console.log("Intro status saved to localStorage");
      } catch (e) {
          console.error("Failed to save intro status to localStorage:", e);
      }
      // Опционально: диспатчим action в Redux, если он используется
      // dispatch(setIntroCompleted(true));
    }, 500); // Время анимации исчезновения fade-out
  }, [onComplete, dispatch]);

  // Обработка свайпов (Touch)
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = 0;
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current !== 0) {
        touchEndX.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === 0) return;
    const minSwipeDistance = 50;
    const isTap = touchEndX.current === 0;
    const swipeDistance = isTap ? 0 : touchStartX.current - touchEndX.current;

    touchStartX.current = 0;
    touchEndX.current = 0;

    if (!isTap && Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) { // Свайп влево
        if (currentSlide === slides.length - 1) completeIntro(); else nextSlide();
      } else { // Свайп вправо
        prevSlide();
      }
    } else { // Тап или короткий свайп
      if (currentSlide === slides.length - 1) completeIntro(); else nextSlide();
    }
  }, [currentSlide, completeIntro, nextSlide, prevSlide]); // Добавляем зависимости

  // Обработка перетаскивания (Mouse)
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    mouseStartX.current = e.clientX;
    mouseEndX.current = 0;
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    mouseEndX.current = e.clientX;
  };

  const handleMouseUp = useCallback(() => { // useCallback чтобы добавить в зависимости useEffect
    if (!isDragging) return;
    setIsDragging(false);
    const minSwipeDistance = 50;
    const isClick = mouseEndX.current === 0;
    const swipeDistance = isClick ? 0 : mouseStartX.current - mouseEndX.current;

    mouseStartX.current = 0;
    mouseEndX.current = 0;

    if (!isClick && Math.abs(swipeDistance) > minSwipeDistance) {
       if (swipeDistance > 0) { // Drag влево
         if (currentSlide === slides.length - 1) completeIntro(); else nextSlide();
       } else { // Drag вправо
         prevSlide();
       }
    } else { // Клик или короткий drag
       if (currentSlide === slides.length - 1) completeIntro(); else nextSlide();
    }
  }, [isDragging, currentSlide, completeIntro, nextSlide, prevSlide]); // Добавляем зависимости

  const handleMouseLeave = useCallback(() => { // useCallback чтобы добавить в зависимости useEffect
     if (isDragging) {
         handleMouseUp(); // Завершаем drag если мышь ушла
     }
  }, [isDragging, handleMouseUp]); // Добавляем зависимости

  // Обработка нажатий клавиш
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        if (currentSlide === slides.length - 1) {
          completeIntro();
        } else {
          nextSlide();
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        prevSlide();
      } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
        completeIntro();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // Зависимости для хука нажатий клавиш
  }, [currentSlide, completeIntro, nextSlide, prevSlide]);

  // Добавление/удаление обработчиков событий touch/mouse
  useEffect(() => {
    const sliderElement = sliderRef.current;
    if (sliderElement) {
      // Touch events
      sliderElement.addEventListener('touchstart', handleTouchStart, { passive: true });
      sliderElement.addEventListener('touchmove', handleTouchMove, { passive: true });
      sliderElement.addEventListener('touchend', handleTouchEnd);
      // Mouse events
      sliderElement.addEventListener('mousedown', handleMouseDown);
      sliderElement.addEventListener('mousemove', handleMouseMove);
      sliderElement.addEventListener('mouseup', handleMouseUp);
      sliderElement.addEventListener('mouseleave', handleMouseLeave);
    }
    return () => {
      if (sliderElement) {
        // Touch events cleanup
        sliderElement.removeEventListener('touchstart', handleTouchStart);
        sliderElement.removeEventListener('touchmove', handleTouchMove);
        sliderElement.removeEventListener('touchend', handleTouchEnd);
        // Mouse events cleanup
        sliderElement.removeEventListener('mousedown', handleMouseDown);
        sliderElement.removeEventListener('mousemove', handleMouseMove);
        sliderElement.removeEventListener('mouseup', handleMouseUp);
        sliderElement.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  // Добавляем все используемые колбэки в зависимости
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave]);

  return (
    <div className={`intro-slider-overlay ${fadeOut ? 'fade-out' : ''}`} ref={sliderRef}>
      <div className="intro-slider-container">
        <div className="intro-slider-content">
          {/* Предыдущий слайд */}
          <div className="intro-slider-image-container prev-slide">
            <img
              key={`prev-${getPrevSlideIndex()}`}
              src={getImageUrlWithCacheBuster(slides[getPrevSlideIndex()])}
              alt={`Слайд ${getPrevSlideIndex() + 1}`}
              className="intro-slider-image"
              onError={(e) => handleImageError(e, getPrevSlideIndex())}
              loading="lazy" // Добавим lazy loading
            />
          </div>

          {/* Следующий слайд */}
          <div className="intro-slider-image-container next-slide">
            <img
              key={`next-${getNextSlideIndex()}`}
              src={getImageUrlWithCacheBuster(slides[getNextSlideIndex()])}
              alt={`Слайд ${getNextSlideIndex() + 1}`}
              className="intro-slider-image"
              onError={(e) => handleImageError(e, getNextSlideIndex())}
              loading="lazy" // Добавим lazy loading
            />
          </div>

          {/* Текущий слайд */}
          <div className={`intro-slider-image-container ${animationClass}`}>
            <img
              key={`current-${currentSlide}`}
              src={getImageUrlWithCacheBuster(slides[currentSlide])}
              alt={`Слайд ${currentSlide + 1}`}
              className="intro-slider-image"
              onError={(e) => handleImageError(e, currentSlide)}
              // loading="eager" // Основной слайд грузим сразу
            />
          </div>

          {/* Точки и подсказка */}
          <div className="intro-slider-dots-container">
            <div className="intro-slider-dots">
              {slides.map((_, index) => (
                <div
                  key={index}
                  className={`intro-slider-dot ${currentSlide === index ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  role="button" // Для доступности
                  aria-label={`Перейти к слайду ${index + 1}`}
                  tabIndex={0} // Для доступности
                  onKeyPress={(e) => e.key === 'Enter' && goToSlide(index)} // Для доступности
                />
              ))}
            </div>
            <div className="intro-slider-hint">
              {currentSlide === slides.length - 1 ? 'Нажмите, чтобы продолжить' : 'Свайп для навигации'}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default IntroSlider;